import { useSearch } from "@tanstack/react-router";

import type { Page } from "../../api";
import { TabPage } from "../../PageFrame";
import { Legend, Swatch } from "../bars";
import { VBars, type BarPoint } from "../charts";
import type { PageProps } from "../index";
import { money, Note, PageHead } from "../kit";
import { landedLabel, latestPurchase, NO_PURCHASE, purchaseLine } from "../landed";
import { ALL_ROWS, pageExtra } from "../params";
import { VariantPage } from "../variant";

export interface OfferRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  funnel: string;
  variant: string;
  incoterm: string | null;
  quote_date: string | null;
  unit_price_gbp: number | null;
  unit_price_native: number | null;
  currency: string | null;
  quantity: number | null;
  moq: number | null;
  status: string;
  latest_purchase_price_gbp: number | null;
  latest_purchase_date: string | null;
  latest_purchase_supplier: string | null;
}

/** One bar per offer with a price, dearest first; `mark` is the option drawn in ink. */
export function offerPoints(rows: OfferRow[], mark: string | null = null): BarPoint[] {
  return rows
    .filter((row) => row.unit_price_gbp != null)
    .sort((a, b) => (b.unit_price_gbp ?? 0) - (a.unit_price_gbp ?? 0))
    .map((row) => ({
      label: landedLabel(row),
      value: row.unit_price_gbp,
      option: { optionId: row.option_id, itemId: row.item_id },
      ...(mark !== null && row.option_id === mark ? { marked: true } : {}),
    }));
}

/** The option Option Overview's Offers button came from, carried as `mark` (not a filter: every offer still shows). */
export function readMark(search: Record<string, unknown>): string | null {
  return typeof search.mark === "string" && search.mark !== "" ? search.mark : null;
}

export function IngredientsFunnelBody({ rows, mark = null }: { rows: OfferRow[]; mark?: string | null }) {
  const purchase = latestPurchase(rows[0]);
  const points = offerPoints(rows, mark);
  const marked = points.some((point) => point.marked) ? rows.find((row) => row.option_id === mark) : undefined;
  return (
    <>
      <Legend>
        <Swatch className="bg-ks-purple">Offers (GBP/kg)</Swatch>
        {marked ? <Swatch className="bg-ks-ink">{marked.option_name}</Swatch> : null}
        {purchase ? (
          <Swatch className="h-0 w-[18px] rounded-none border-t-[3px] border-ks-warn">{purchaseLine(purchase)}</Swatch>
        ) : null}
      </Legend>
      {purchase ? null : <Note>{NO_PURCHASE}</Note>}
      <VBars
        title="Offers vs latest purchase"
        slot={48}
        rotate
        points={points}
        format={(value) => value.toFixed(2)}
        from="ingredients_funnel"
        reference={
          purchase
            ? { value: purchase.price, label: `Latest purchase ${money(purchase.price)}`, tone: "warn", dashed: true, width: 2 }
            : undefined
        }
      />
    </>
  );
}

export function IngredientsFunnelPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return (
    <VariantPage tab={tab}>
      <TabPage<Page<OfferRow>>
        tab={tab}
        areaName={areaName}
        extra={{ ...pageExtra(tab.tabKey, search), limit: ALL_ROWS }}
        isEmpty={(data) => data.items.length === 0}
        head={(data) => {
          const first = data?.items[0];
          const purchase = latestPurchase(first);
          return (
            <PageHead
              title={tab.label}
              sub={first ? `${first.variant} · offer price per kg vs what we last paid` : undefined}
              kpi={purchase ? { label: "Latest purchase", value: money(purchase.price) } : null}
            />
          );
        }}
      >
        {(data) => <IngredientsFunnelBody rows={data.items} mark={readMark(search)} />}
      </TabPage>
    </VariantPage>
  );
}
